<script setup>
import DialogDrawer from '@js/components/dialogs/DialogDrawer.vue';
import ComboboxField from '@js/components/fields/ComboboxField.vue';
import SwitchField from '@js/components/fields/SwitchField.vue';
import TextareaField from '@js/components/fields/TextareaField.vue';
import FormColumn from '@js/components/forms/FormColumn.vue';
import FormContent from '@js/components/forms/FormContent.vue';
import FormFooter from '@js/components/forms/FormFooter.vue';
import FormGrid from '@js/components/forms/FormGrid.vue';
import FormRoot from '@js/components/forms/FormRoot.vue';
import { useRoute } from '@js/utils/route';
import { reactive, ref } from 'vue';
import { array, boolean, object, string } from 'yup';

const {
    tenant,
    callback,
    keywords = '',
    disabledKeywords = false,
} = defineProps({
    tenant: {
        type: Object,
        required: true,
    },
    callback: {
        type: Function,
        required: false,
    },
    keywords: {
        type: String,
        required: false,
    },
    disabledKeywords: {
        type: Boolean,
        required: false,
    },
});

const { route } = useRoute();

const websiteVariants = ref([]);

const targetableGoogleWebsites = ref([]);

const targetedLanguages = ref([]);

const countries = ref([]);

const isOpen = ref(false);

const loading = ref(true);

const isSubmitting = ref(false);

const form = reactive({
    keywords,
    isTracked: true,
    importantToFollow: false,
    country: null,
    language: null,
    targets: null,
});

const validationSchema = object({
    keywords: string().required('Ce champ est obligatoire.'),
    isTracked: boolean(),
    importantToFollow: boolean(),
    country: object().nullable(),
    language: object().nullable(),
    targets: array().of(
        array().test(
            'at-least-one-target',
            'Au moins une cible est requise.',
            () => {
                return Object.values(form.targets).some(
                    (targetArray) => targetArray.length > 0,
                );
            },
        ),
    ),
});

function setIsOpen(value) {
    if (value) {
        updateData();
    }

    isOpen.value = value;
}

function setKeywords(value) {
    form.keywords = value;
}

function updateData() {
    loading.value = true;

    axios
        .post(
            route('front.projectManagement.seo.postImportTrackedKeywordsData', {
                tenant: tenant.id,
            }),
        )
        .then((response) => {
            websiteVariants.value = response.data.websiteVariants;
            targetableGoogleWebsites.value =
                response.data.targetableGoogleWebsites;
            targetedLanguages.value = response.data.targetedLanguages;
            countries.value = response.data.countries;

            form.targets = websiteVariants.value.reduce((acc, variant) => {
                acc[variant.id] = [];
                return acc;
            }, {});
        })
        .catch((error) => {
            console.error(error);
        })
        .finally(() => {
            loading.value = false;
        });
}

function submit() {
    isSubmitting.value = true;

    axios
        .post(
            route('front.projectManagement.seo.postImportTrackedKeywords', {
                tenant: tenant.id,
            }),
            {
                keywords: form.keywords
                    .split('\n')
                    .filter((keyword) => keyword.trim() !== ''),
                isTracked: form.isTracked,
                importantToFollow: form.importantToFollow,
                countryId: form.country ? form.country.id : null,
                languageId: form.language ? form.language.id : null,
                targets: Object.fromEntries(
                    Object.entries(form.targets).map(([key, value]) => [
                        key,
                        value.map((item) => item.id),
                    ]),
                ),
            },
        )
        .then(() => {
            setIsOpen(false);

            if (callback) {
                callback();
            }
        })
        .catch((error) => {
            console.error(error);
        })
        .finally(() => {
            isSubmitting.value = false;
        });
}
</script>

<template>
    <slot name="button" :set-is-open="setIsOpen" :set-keywords="setKeywords" />

    <DialogDrawer v-model="isOpen" :loading="loading">
        <template #title> Importer </template>

        <template #full-content>
            <FormRoot
                :validation-schema="validationSchema"
                :submit="submit"
                v-slot="{ errors }"
            >
                <FormColumn>
                    <FormContent>
                        <FormGrid :columns="1">
                            <TextareaField
                                v-model="form.keywords"
                                label="Mots-clés"
                                placeholder="URLs source"
                                field-name="keywords"
                                field-id="keywords"
                                legend="Un mot-clé par ligne"
                                :disabled="disabledKeywords"
                                required
                            />
                        </FormGrid>
                    </FormContent>

                    <FormContent>
                        <FormGrid :columns="2">
                            <SwitchField
                                v-model="form.isTracked"
                                label="Suivi actif"
                                field-name="isTracked"
                                field-id="isTracked"
                            />

                            <SwitchField
                                v-model="form.importantToFollow"
                                label="Important à suivre"
                                field-name="importantToFollow"
                                field-id="importantToFollow"
                            />
                        </FormGrid>
                    </FormContent>

                    <FormContent>
                        <FormGrid :columns="1">
                            <ComboboxField
                                v-model="form.country"
                                :options="countries"
                                field-name="country"
                                field-id="country"
                                label="Pays"
                                filtered-option-key="name"
                                has-empty-option
                            >
                                <template #optionContent="{ option }">
                                    {{ option.name }}
                                </template>
                            </ComboboxField>

                            <ComboboxField
                                v-model="form.language"
                                :options="targetedLanguages"
                                field-name="language"
                                field-id="language"
                                label="Langue"
                                filtered-option-key="name"
                                has-empty-option
                            >
                                <template #optionContent="{ option }">
                                    {{ option.name }}
                                </template>
                            </ComboboxField>
                        </FormGrid>
                    </FormContent>

                    <FormContent>
                        <FormGrid :columns="1">
                            <ComboboxField
                                v-for="websiteVariant in websiteVariants"
                                :key="websiteVariant.id"
                                v-model="form.targets[websiteVariant.id]"
                                filtered-option-key="name"
                                has-empty-option
                                :options="targetableGoogleWebsites"
                                :multiple="true"
                                :field-name="
                                    'targets[' + websiteVariant.id + ']'
                                "
                                :field-id="'targets[' + websiteVariant.id + ']'"
                                :label="
                                    'Cibles' + ' (' + websiteVariant.name + ')'
                                "
                                by="id"
                            >
                                <template #optionContent="{ option }">
                                    {{ option.name }}
                                </template>
                            </ComboboxField>
                        </FormGrid>
                    </FormContent>
                </FormColumn>

                <FormFooter
                    :is-submitting="isSubmitting"
                    :errors="errors"
                    submit-text="Importer"
                    submitting-text="Import en cours..."
                    without-back-button
                />
            </FormRoot>
        </template>
    </DialogDrawer>
</template>
